import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { KeyRound, Loader2, X } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export const FirstLoginPassword = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [dismissed, setDismissed] = useState(sessionStorage.getItem("pw_prompt_dismissed") === "1");
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  if (!user?.must_change_password || dismissed || done) return null;

  const skip = () => {
    sessionStorage.setItem("pw_prompt_dismissed", "1");
    setDismissed(true);
  };

  const submit = async () => {
    if (!form.current) return toast.error(t("currentPasswordRequired"));
    if (form.next.length < 6) return toast.error(t("passwordTooShort"));
    if (form.next !== form.confirm) return toast.error(t("passwordsDontMatch"));
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success(t("passwordChanged"));
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div data-testid="first-login-password-dialog" className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-500" />{t("firstLoginTitle")}
          </h2>
          <button data-testid="pw-dialog-close" onClick={skip} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">{t("firstLoginHint")}</p>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("currentPassword")}</label>
            <input data-testid="pw-current" type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("newPassword")}</label>
              <input data-testid="pw-new" type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("confirmPassword")}</label>
              <input data-testid="pw-confirm" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button data-testid="pw-skip" onClick={skip} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("notNow")}</button>
          <button data-testid="pw-submit" disabled={busy} onClick={submit} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("changePassword")}
          </button>
        </div>
      </div>
    </div>
  );
};
